/**
 * 時區工具模組
 * 提供 UTC 與 Asia/Taipei 時區轉換功能
 * 合併前後端共用的時間工具函數
 */
const TAIWAN_TIMEZONE = 'Asia/Taipei';
// ==================== 後端常用函數 ====================
/**
 * 取得當前 UTC 時間的 ISO 字串
 */
export const getCurrentUTC = () => {
    return new Date().toISOString();
};
/**
 * 取得當前台灣時間的 ISO 字串
 */
export const getCurrentTaiwanTime = () => {
    const now = new Date();
    // 轉換為台灣時區的 Date 物件
    const taiwanDate = new Date(now.toLocaleString('en-US', { timeZone: TAIWAN_TIMEZONE }));
    return taiwanDate.toISOString();
};
/**
 * 取得當前台北日期字串（YYYY-MM-DD），用於電子券適用日期比對等
 */
export const getTodayTaipeiDateString = () => {
    return new Date().toLocaleDateString('en-CA', {
        timeZone: TAIWAN_TIMEZONE,
    });
};
/**
 * 取得當前台北時間字串（HH:mm:ss），用於電子券適用時段比對
 */
export const getCurrentTaipeiTimeString = () => {
    return new Date().toLocaleTimeString('en-GB', {
        timeZone: TAIWAN_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};
/**
 * 將 HH:mm 或 HH:mm:ss 正規化為 HH:mm:ss（供儲存或字串比對）
 */
export const normalizeTimeToHhMmSs = (s) => {
    const parts = s.trim().split(':');
    if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
    }
    if (parts.length === 3) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
    }
    return s;
};
/**
 * 將 UTC 時間轉換為台灣時間
 */
export const utcToTaiwan = (utcDate) => {
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    // 轉換為台灣時區的 Date 物件
    const taiwanDate = new Date(date.toLocaleString('en-US', { timeZone: TAIWAN_TIMEZONE }));
    return taiwanDate.toISOString();
};
/**
 * 取得當前時間資訊（包含 UTC 和台灣時間）
 */
export const getCurrentTimeInfo = () => {
    const nowUTC = getCurrentUTC();
    const nowTaiwan = getCurrentTaiwanTime();
    return {
        tz: TAIWAN_TIMEZONE,
        now_utc: nowUTC,
        now_local: nowTaiwan,
    };
};
/**
 * 取得當前台北時間的 SQLite 格式字串
 * 用於資料庫插入和更新操作
 */
export const getCurrentTaipeiSQLite = () => {
    const now = new Date();
    const taipeiTime = new Date(now.toLocaleString('en-US', { timeZone: TAIWAN_TIMEZONE }));
    return taipeiTime.toISOString().replace('T', ' ').replace('Z', '');
};
/**
 * 將台北時間（當地時間）轉換為 UTC 時間（用於資料庫儲存和查詢）
 *
 * 原則：
 * - 用戶輸入的日期時間都是當地時間（Asia/Taipei）
 * - 如果只輸入日期（YYYY-MM-DD），視為當天的 00:00:00（整天開始）
 * - 轉換為 UTC ISO 字串後存儲到資料庫
 *
 * @param taipeiTime 台北時間字串，格式可以是：
 *   - YYYY-MM-DD（日期，視為 00:00:00）
 *   - YYYY-MM-DDTHH:mm（日期時間，秒數為 00）
 *   - YYYY-MM-DDTHH:mm:ss（完整日期時間）
 *   - 已包含時區的 ISO 格式
 * @returns UTC 格式的 ISO 字串（例如：2026-01-14T00:00:00.000Z）
 */
export const taipeiToUtc = (taipeiTime) => {
    if (!taipeiTime)
        return '';
    // 如果輸入是日期格式 (YYYY-MM-DD)，添加台北時區的開始時間（00:00:00）
    if (/^\d{4}-\d{2}-\d{2}$/.test(taipeiTime)) {
        // 將台北時間的 00:00:00 轉換為 UTC
        const date = new Date(`${taipeiTime}T00:00:00+08:00`);
        return date.toISOString();
    }
    // 如果輸入是日期時間格式但沒有秒數 (YYYY-MM-DDTHH:mm)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(taipeiTime)) {
        const date = new Date(`${taipeiTime}:00+08:00`);
        return date.toISOString();
    }
    // 如果輸入是日期時間格式但沒有時區 (YYYY-MM-DDTHH:mm:ss)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(taipeiTime)) {
        const date = new Date(`${taipeiTime}+08:00`);
        return date.toISOString();
    }
    // 如果已經包含時區信息，直接轉換
    if (taipeiTime.includes('+') ||
        taipeiTime.includes('-') ||
        taipeiTime.endsWith('Z')) {
        const date = new Date(taipeiTime);
        return date.toISOString();
    }
    // 如果已經是 UTC ISO 格式，直接返回
    return taipeiTime;
};
/**
 * 將台北時間（當地時間）轉換為 UTC 結束時間（用於日期範圍查詢）
 *
 * 原則：
 * - 用戶輸入的日期時間都是當地時間（Asia/Taipei）
 * - 如果只輸入日期（YYYY-MM-DD），視為當天的 23:59:59.999（整天結束）
 * - 轉換為 UTC ISO 字串後用於資料庫查詢
 *
 * @param taipeiTime 台北時間字串，格式可以是：
 *   - YYYY-MM-DD（日期，視為 23:59:59.999）
 *   - YYYY-MM-DDTHH:mm（日期時間，秒數為 59）
 *   - YYYY-MM-DDTHH:mm:ss（完整日期時間）
 *   - 已包含時區的 ISO 格式
 * @returns UTC 格式的 ISO 字串（例如：2026-01-14T15:59:59.999Z）
 */
export const taipeiToUtcEnd = (taipeiTime) => {
    if (!taipeiTime)
        return '';
    // 如果輸入是日期格式 (YYYY-MM-DD)，添加台北時區的結束時間（23:59:59.999）
    if (/^\d{4}-\d{2}-\d{2}$/.test(taipeiTime)) {
        // 將台北時間的 23:59:59.999 轉換為 UTC
        const date = new Date(`${taipeiTime}T23:59:59.999+08:00`);
        return date.toISOString();
    }
    // 如果輸入是日期時間格式但沒有秒數 (YYYY-MM-DDTHH:mm)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(taipeiTime)) {
        const date = new Date(`${taipeiTime}:59.999+08:00`);
        return date.toISOString();
    }
    // 如果輸入是日期時間格式但沒有時區 (YYYY-MM-DDTHH:mm:ss)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(taipeiTime)) {
        const date = new Date(`${taipeiTime}.999+08:00`);
        return date.toISOString();
    }
    // 如果已經包含時區信息，直接轉換
    if (taipeiTime.includes('+') ||
        taipeiTime.includes('-') ||
        taipeiTime.endsWith('Z')) {
        const date = new Date(taipeiTime);
        return date.toISOString();
    }
    // 如果已經是 UTC ISO 格式，直接返回
    return taipeiTime;
};
/**
 * 將 UTC 時間轉換為台北時間（當地時間）ISO 字串
 *
 * 原則：
 * - 從資料庫取出的時間是 UTC 格式
 * - 第一步就是轉換為當地時間（Asia/Taipei）
 * - 返回 ISO 格式字串，用於 API 回應或前端顯示
 *
 * @param utcTime UTC 時間字串（Date.toISOString() 格式或 Date 物件）
 * @returns 台北時間的 ISO 字串（YYYY-MM-DDTHH:mm:ss+08:00 格式）
 */
export const utcToTaipei = (utcTime) => {
    if (!utcTime)
        return '';
    // 處理 Date 物件
    const date = utcTime instanceof Date ? utcTime : new Date(utcTime);
    // 如果時間字符串沒有時區信息，假設它是 UTC 時間
    if (typeof utcTime === 'string' &&
        !utcTime.includes('Z') &&
        !utcTime.includes('+') &&
        !utcTime.includes('-')) {
        const dateStr = utcTime + 'Z'; // 添加 UTC 標識
        const dateObj = new Date(dateStr);
        // 轉換為台北時間 (UTC+8)
        const taipeiTime = new Date(dateObj.getTime() + 8 * 60 * 60 * 1000);
        // 格式化為 ISO 字串（YYYY-MM-DDTHH:mm:ss+08:00）
        const year = taipeiTime.getUTCFullYear();
        const month = String(taipeiTime.getUTCMonth() + 1).padStart(2, '0');
        const day = String(taipeiTime.getUTCDate()).padStart(2, '0');
        const hours = String(taipeiTime.getUTCHours()).padStart(2, '0');
        const minutes = String(taipeiTime.getUTCMinutes()).padStart(2, '0');
        const seconds = String(taipeiTime.getUTCSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+08:00`;
    }
    // 轉換為台北時間 (UTC+8)
    const taipeiTime = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    // 格式化為 ISO 字串（YYYY-MM-DDTHH:mm:ss+08:00）
    const year = taipeiTime.getUTCFullYear();
    const month = String(taipeiTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(taipeiTime.getUTCDate()).padStart(2, '0');
    const hours = String(taipeiTime.getUTCHours()).padStart(2, '0');
    const minutes = String(taipeiTime.getUTCMinutes()).padStart(2, '0');
    const seconds = String(taipeiTime.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}+08:00`;
};
// ==================== 前端常用函數 ====================
/**
 * 將日期格式轉換為 UTC 的 ISO 8601 格式
 * @param dateStr 日期字符串，格式為 YYYY-MM-DD 或已包含時間的 ISO 格式
 * @param isEndDate 是否為結束日期，結束日期會設為 23:59:59
 * @returns UTC 格式的 ISO 字符串
 */
export function convertToUTCISO(dateStr, isEndDate = false) {
    if (!dateStr)
        return '';
    // 如果已經是完整的 datetime 格式，直接返回
    if (dateStr.includes('T')) {
        return dateStr;
    }
    // 如果是純日期格式，添加時間部分並轉換為 UTC
    const timeStr = isEndDate ? 'T23:59:59+08:00' : 'T00:00:00+08:00';
    const date = new Date(`${dateStr}${timeStr}`); // 先轉為台北時間
    return date.toISOString(); // 轉為 UTC ISO 格式
}
/**
 * 將日期格式轉換為台北時間的 ISO 8601 格式（用於顯示）
 * @param dateStr 日期字符串，格式為 YYYY-MM-DD 或已包含時間的 ISO 格式
 * @param isEndDate 是否為結束日期，結束日期會設為 23:59:59
 * @returns 台北時間格式的 ISO 字符串
 */
export function convertToTaipeiISO(dateStr, isEndDate = false) {
    if (!dateStr)
        return '';
    // 如果已經是完整的 datetime 格式，直接返回
    if (dateStr.includes('T')) {
        return dateStr;
    }
    // 如果是純日期格式，添加時間部分
    const timeStr = isEndDate ? 'T23:59:59+08:00' : 'T00:00:00+08:00';
    return `${dateStr}${timeStr}`;
}
/**
 * 格式化日期顯示（僅顯示日期部分，台北時間）
 * @param dateStr ISO 日期字符串
 * @returns 格式化的日期字符串 YYYY-MM-DD
 */
export function formatDateOnly(dateStr) {
    if (!dateStr)
        return '';
    try {
        const date = new Date(dateStr);
        // 轉換為台北時間 (UTC+8)
        const taipeiDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
        return taipeiDate.toISOString().split('T')[0];
    }
    catch {
        return dateStr.split('T')[0];
    }
}
/**
 * 格式化日期時間顯示（台北時間）
 * @param dateStr ISO 日期字符串（UTC 時間或已包含時區的台北時間）
 * @param showTime 是否顯示時間，預設為 false
 * @returns 格式化的日期時間字符串（台北時間）
 */
export function formatDateTime(dateStr, showTime = false) {
    if (!dateStr)
        return '';
    try {
        // 如果已經是台北時間格式（包含 +08:00），直接提取時間部分
        if (dateStr.includes('+08:00')) {
            if (showTime) {
                // 從 2025-12-18T09:37:14+08:00 提取 2025-12-18 09:37:14
                return dateStr.replace('T', ' ').replace('+08:00', '');
            }
            else {
                // 從 2025-12-18T09:37:14+08:00 提取 2025-12-18
                return dateStr.split('T')[0];
            }
        }
        // 否則，假設是 UTC 時間，使用 toLocaleString 轉換為台北時間
        const date = new Date(dateStr);
        if (showTime) {
            // 格式：YYYY-MM-DD HH:mm:ss
            const options = {
                timeZone: 'Asia/Taipei',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            };
            const formatted = date.toLocaleString('zh-TW', options);
            // 轉換格式：2025/12/18 09:05:01 -> 2025-12-18 09:05:01
            return formatted.replace(/\//g, '-').replace(/,/g, '');
        }
        else {
            // 格式：YYYY-MM-DD
            const options = {
                timeZone: 'Asia/Taipei',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            };
            const formatted = date.toLocaleString('zh-TW', options);
            // 轉換格式：2025/12/18 -> 2025-12-18
            return formatted.replace(/\//g, '-');
        }
    }
    catch {
        return showTime ? dateStr : dateStr.split('T')[0];
    }
}
/**
 * 驗證日期範圍
 * @param startDate 開始日期
 * @param endDate 結束日期
 * @returns 驗證結果和錯誤訊息
 */
export function validateDateRange(startDate, endDate) {
    if (!startDate || !endDate) {
        return { isValid: true }; // 允許空值
    }
    const start = new Date(convertToUTCISO(startDate));
    const end = new Date(convertToUTCISO(endDate, true));
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return { isValid: false, error: '日期格式無效' };
    }
    if (start > end) {
        return { isValid: false, error: '結束日期不得早於開始日期' };
    }
    return { isValid: true };
}
/**
 * 檢查日期是否在指定範圍內
 * @param date 要檢查的日期
 * @param startDate 開始日期
 * @param endDate 結束日期
 * @returns 是否在範圍內
 */
export function isDateInRange(date, startDate, endDate) {
    if (!date || !startDate || !endDate)
        return true;
    const checkDate = new Date(convertToUTCISO(date));
    const start = new Date(convertToUTCISO(startDate));
    const end = new Date(convertToUTCISO(endDate, true));
    return checkDate >= start && checkDate <= end;
}
/** 一日毫秒數（用於試用期剩餘天數等計算） */
export const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * 將 UTC ISO 字串格式為台北日期顯示用 YYYY/MM/DD（Lite 試用期到期日等）
 */
export function formatDateTaipeiSlash(utcIso) {
    const s = utcToTaipei(utcIso);
    if (!s)
        return '';
    return s.slice(0, 10).replace(/-/g, '/');
}
